/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import { AssistantIcon } from 'modules/common/components';
import { Grid, IconButton, Typography } from '@mui/material';
import CollapseIcon from 'modules/common/components/collapse-icon';
import { ENVIRONMENT } from 'config';
import { EXPORT_PDF_BUTTON_STYLE } from 'modules/dashboard/components/tab-container/workbook-report/style';
import MuiTabular from 'modules/common/components/mui-tabular';
import GetAppIcon from '@mui/icons-material/GetApp';
import { isEmpty } from '../../../../../../../../common/helpers/object';
import { paceActions } from '../../../../slice';
import {
  selectIsForecast,
  selectPaceCumulativeView,
  selectPaceLagAligned,
  selectPaceLoading,
  selectPaceTabularData,
  selectPaceTimeTabularFilterList,
  selectRevenueIsDisplayPT,
} from '../../../../selectors';
import TabularFilterHeader from '../filter-header';
import { getPaceTimeSchema, getPaceTimeTitle } from '../../../../functions';
import usePaceTimeTabular from '../../../../hooks/use-pace-time-tabular';
import { CERULEAN_TABLE_SECONDARY_TEXT_COLOR } from '../../../../constants';
/**
 * Pace Time tabular visual report with filters
 */
const CeruleanPaceTimeTabularWidget = ({ setIsTimeLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectPaceTimeTabularFilterList);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayPT);
  const paceTabularData = useSelector(selectPaceTabularData);
  const isPaceDataLoading = useSelector(selectPaceLoading);
  const isForecast = useSelector(selectIsForecast);
  //
  const tableRef = useRef();
  const { headerData, interpreterAgentData } = usePaceTimeTabular(
    setIsTimeLoaded,
    revenueIsDisplay
  );
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [isInterpreterExporting, setIsInterpreterExporting] = useState(false);
  //
  return (
    !isEmpty(tabularFilterList) && (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        className={styles.mainGrid}
        mb={10}
      >
        <Grid className={styles.paceVisualGrid}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ minHeight: 40, px: 2 }}
          >
            <Grid item>
              <Typography>{getPaceTimeTitle(paceCumulative, paceLagAligned)}</Typography>
            </Grid>

            <Grid item>
              <Grid container alignItems="center">
                <>
                  <Typography>Export Data</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsExcelExporting(true)}
                    disabled={
                      !revenueIsDisplay ||
                      isExcelExporting ||
                      isPaceDataLoading ||
                      !paceTabularData?.length
                    }
                    sx={EXPORT_PDF_BUTTON_STYLE}
                    id="pace-js-export-button"
                  >
                    <GetAppIcon />
                  </IconButton>
                </>
                {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                  <Grid item mt={1} mr={4}>
                    <AssistantIcon
                      exportData={interpreterAgentData}
                      visualName={getPaceTimeTitle(paceCumulative, paceLagAligned)}
                      isProcessing={isInterpreterExporting}
                      setIsProcessing={setIsInterpreterExporting}
                      disabled={
                        !revenueIsDisplay ||
                        isExcelExporting ||
                        isInterpreterExporting ||
                        isPaceDataLoading
                      }
                      isCerulean
                      getExcelExportData={tableRef?.current?.getExcelData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  </Grid>
                )}

                <CollapseIcon
                  isDisplay={revenueIsDisplay}
                  onClick={() => dispatch(paceActions.setRevenueIsDisplayPT(!revenueIsDisplay))}
                />
              </Grid>
            </Grid>
          </Grid>
          {revenueIsDisplay && (
            <Grid px={2}>
              {!paceTabularData ? (
                <Grid container justifyContent="center" alignItems="center" py={5}>
                  <Typography fontWeight={500}>Loading Data...</Typography>
                </Grid>
              ) : (
                <MuiTabular
                  ref={tableRef}
                  columns={getPaceTimeSchema(paceCumulative, isForecast, paceLagAligned)}
                  isProcessing={isExcelExporting}
                  isLoadingData={isPaceDataLoading}
                  setIsProcessing={setIsExcelExporting}
                  secondaryTextColor={CERULEAN_TABLE_SECONDARY_TEXT_COLOR}
                  customAggregationRowStyle={{ paddingRight: 1 }}
                  isAggregate
                  headerComponent={
                    <TabularFilterHeader
                      hotelName={headerData?.hotelName}
                      sections={headerData?.sections}
                      comparisonHotelName={headerData?.comparisonHotelName}
                      chunkSize={2}
                    />
                  }
                  data={paceTabularData}
                  headerFiltersInfo={headerData}
                  fileName={
                    paceCumulative && paceLagAligned
                      ? 'PVT_Cumulative_Lag_Aligned'
                      : paceCumulative && !paceLagAligned
                      ? 'PVT_Cumulative'
                      : paceLagAligned && !paceCumulative
                      ? 'PVT_Lag_Aligned'
                      : 'PVT_Daily'
                  }
                />
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    )
  );
};
//
export default CeruleanPaceTimeTabularWidget;
